import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLazyQuery, useMutation } from '@apollo/client';
import { styled } from '@mui/material/styles';
import {
  GET_PRIME_REGULARISATION_BY_TAM_AND_MONTH,
  UPSERT_REGULARISATION_BY_ADMIN,
} from '@bloomays-lib/adapter.api-talent';
import { Button, InputField, Modal, SingleLineText, TitlePart } from '@bloomays-lib/ui.shared';
import { convertDateToStringFormat } from '@bloomays-lib/utils.shared';
import { useTranslation } from 'react-i18next';
import { errorLogger } from '../../helpers/error';
import { notify } from '../../helpers/toastify';
import { GetPrimeRegularisationByTamAndMonthQueryData } from '../../types/primes';
import { formatPrimeValidationDate } from '../../utils/monthlyPrime.utils';

export type TamRegularisationModalProps = {
  open: boolean;
  onClose: () => void;
  onSaved?: () => void;
  tamEmail: string;
  tamDisplayName: string;
  referenceDate: Date;
};

const hasAtMostTwoDecimalPlaces = (total: number): boolean => total === Math.round(total * 100) / 100;

const parseAmountInput = (raw: string): number => {
  const normalized = raw.trim().replace(',', '.');
  if (normalized === '' || normalized === '-') {
    return Number.NaN;
  }
  return Number(normalized);
};

const TamRegularisationModal = ({
  open,
  onClose,
  onSaved,
  tamEmail,
  tamDisplayName,
  referenceDate,
}: TamRegularisationModalProps) => {
  const { t } = useTranslation(['primes']);
  const monthYear = useMemo(() => convertDateToStringFormat(referenceDate, 'MM/yyyy'), [referenceDate]);
  const [amount, setAmount] = useState('');
  const [invalidAmountMessage, setInvalidAmountMessage] = useState<string | undefined>();

  const [loadRegularisation, { loading: loadingRegularisation, data: loadData, error: loadError }] =
    useLazyQuery<GetPrimeRegularisationByTamAndMonthQueryData>(GET_PRIME_REGULARISATION_BY_TAM_AND_MONTH, {
      context: { clientName: 'api.talents' },
      fetchPolicy: 'network-only',
    });

  const [upsertRegularisation, { loading: isSaving }] = useMutation(UPSERT_REGULARISATION_BY_ADMIN, {
    context: { clientName: 'api.talents' },
    onCompleted: () => {
      onSaved?.();
      onClose();
    },
    onError: (mutationError) => {
      errorLogger(mutationError, { extraInfos: 'upsertRegularisationByAdmin failed in TamRegularisationModal' });
      notify('error', t('regularisationSaveError'), mutationError);
    },
  });

  const loadedRegularisation = loadData?.getPrimeRegularisationByTamAndMonth ?? null;

  const traceability = useMemo(() => {
    if (!loadedRegularisation) {
      return null;
    }
    const author = loadedRegularisation.modifiedAt
      ? loadedRegularisation.modifiedBy ?? loadedRegularisation.createdBy
      : loadedRegularisation.createdBy;
    const dateRaw = loadedRegularisation.modifiedAt ?? loadedRegularisation.createdAt;
    return {
      author,
      date: formatPrimeValidationDate(dateRaw),
    };
  }, [loadedRegularisation]);

  useEffect(() => {
    if (!open) {
      return;
    }
    setAmount('');
    setInvalidAmountMessage(undefined);
    void loadRegularisation({
      variables: { tamEmail, monthYear },
    });
  }, [loadRegularisation, monthYear, open, tamEmail]);

  useEffect(() => {
    if (!open || loadingRegularisation || loadError) {
      return;
    }
    if (loadedRegularisation === null) {
      setAmount('');
      return;
    }
    setAmount(String(loadedRegularisation.total));
  }, [loadError, loadedRegularisation, loadingRegularisation, open]);

  useEffect(() => {
    if (!open || !loadError) {
      return;
    }
    errorLogger(loadError, { extraInfos: 'getPrimeRegularisationByTamAndMonth failed in TamRegularisationModal' });
    notify('error', t('regularisationLoadError'), loadError);
  }, [loadError, open, t]);

  const handleClose = useCallback(() => {
    if (!isSaving) {
      onClose();
    }
  }, [isSaving, onClose]);

  const isSaveBlocked = loadingRegularisation || isSaving || loadError !== undefined;

  const handleSave = useCallback(() => {
    if (loadError) {
      return;
    }
    const total = parseAmountInput(amount);
    if (!Number.isFinite(total) || !hasAtMostTwoDecimalPlaces(total)) {
      setInvalidAmountMessage(t('regularisationInvalidAmount'));
      return;
    }
    setInvalidAmountMessage(undefined);
    void upsertRegularisation({
      variables: {
        input: {
          tamEmail,
          monthYear,
          total,
        },
      },
    });
  }, [amount, loadError, monthYear, t, tamEmail, upsertRegularisation]);

  return (
    <Modal open={open} onClose={handleClose} additionnalCSS={{ width: '480px', padding: '32px', borderRadius: '20px' }}>
      <ModalContent>
        <TitlePart textTitle={t('regularisationModalTitle', { tamName: tamDisplayName })} variant="heading6" />
        <InputField
          label={t('regularisationAmountLabel')}
          value={amount}
          type="number"
          handleChange={(event) => setAmount(event.target.value)}
          error={invalidAmountMessage !== undefined}
          helperText={invalidAmountMessage}
          readOnly={isSaveBlocked}
        />
        {traceability ? (
          <TraceabilityText
            variant="body2Regular"
            text={t('regularisationRecordedBy', { author: traceability.author, date: traceability.date })}
          />
        ) : null}
        <ActionsRow>
          <Button
            buttonType="button"
            textButton={t('regularisationSave')}
            size="medium"
            disable={isSaveBlocked}
            onClick={handleSave}
          />
        </ActionsRow>
      </ModalContent>
    </Modal>
  );
};

export default TamRegularisationModal;

const ModalContent = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(2),
  marginTop: theme.spacing(2),
}));

const TraceabilityText = styled(SingleLineText)(({ theme }) => ({
  color: theme.palette.neutral[60],
}));

const ActionsRow = styled('div')(({ theme }) => ({
  display: 'flex',
  justifyContent: 'flex-end',
  gap: theme.spacing(1.5),
  marginTop: theme.spacing(1),
}));
